/**
 * @file apps/web/src/pages/AcceptInvitePage.tsx
 * @description Where a Supabase invite email lands (`/auth/accept-invite`). The
 * link carries a one-time session in the URL hash, which the Supabase client
 * picks up on load; this page then has the invitee choose a password and signs
 * them in. The first authenticated API call (GET /auth/me) is what CLAIMS the
 * invite on the server — it creates their profile and project membership — so a
 * missing or revoked invite surfaces here as a clear message rather than a
 * silent login failure. The invitee also enters their first and last name, saved
 * to their profile (PATCH /auth/me) so the portal can greet them by name.
 * Client roles land in the portal, everyone else in the app.
 * Public route: no session is required to reach it.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, KeyRound } from 'lucide-react';
import { useAuth, type AuthUser } from '../lib/auth.js';
import { usePortalAuth } from '../lib/portalAuth.js';
import { authApi, ApiError } from '../lib/api.js';
import { supabase } from '../lib/supabase.js';
import { landingPathFor, parseAuthHashError } from '../lib/projectMembers.js';

const MIN_PASSWORD_LENGTH = 8;

type Phase =
  | { kind: 'checking' }
  | { kind: 'ready'; email: string; accessToken: string }
  | { kind: 'invalid'; message: string };

export function AcceptInvitePage() {
  const { setUser } = useAuth();
  const { setUser: setPortalUser } = usePortalAuth();
  const navigate = useNavigate();

  const [phase, setPhase]       = useState<Phase>({ kind: 'checking' });
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [error, setError]       = useState('');
  const [saving, setSaving]     = useState(false);

  // Read the session the invite link established. The Supabase client consumes
  // the URL hash during its own initialisation, so a session may already exist
  // by the time this effect runs or arrive a moment later via onAuthStateChange.
  useEffect(() => {
    const hashError = parseAuthHashError(window.location.hash);
    if (hashError) {
      setPhase({ kind: 'invalid', message: hashError });
      return;
    }

    let settled = false;
    const accept = (email: string | undefined, accessToken: string) => {
      if (settled) return;
      settled = true;
      setPhase({ kind: 'ready', email: email ?? '', accessToken });
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.access_token) accept(session.user.email, session.access_token);
    });
    void supabase.auth.getSession().then(({ data }) => {
      if (data.session?.access_token) accept(data.session.user.email, data.session.access_token);
    });
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        setPhase({ kind: 'invalid', message: 'This invite link is not valid or has already been used. Ask your project admin to resend it.' });
      }
    }, 6000);

    return () => { subscription.unsubscribe(); clearTimeout(timer); };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (phase.kind !== 'ready') return;
    if (!firstName.trim() || !lastName.trim()) { setError('Enter your first and last name.'); return; }
    if (password.length < MIN_PASSWORD_LENGTH) { setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`); return; }
    if (password !== confirm) { setError('The two passwords do not match.'); return; }

    setSaving(true);
    setError('');
    try {
      const { data, error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw new Error(updateError.message);
      // updateUser keeps the session; use its freshest access token.
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token ?? phase.accessToken;

      let me;
      try {
        me = await authApi.me({ Authorization: `Bearer ${accessToken}` });
      } catch (err) {
        await supabase.auth.signOut();
        if (err instanceof ApiError && err.message === 'User profile not found') {
          throw new Error(
            `We couldn't find an active invite for ${data.user?.email ?? 'this email'}. It may have been revoked or expired — ask your project admin to invite you again.`,
          );
        }
        throw err;
      }

      // The profile was just created by the claim with no name; save the one entered.
      // A failure here must not block sign-in — the name can be set later.
      const fullName = `${firstName.trim()} ${lastName.trim()}`;
      let savedName = me.fullName;
      try {
        await authApi.updateMe({ fullName }, { Authorization: `Bearer ${accessToken}` });
        savedName = fullName;
      } catch { /* keep the claim's profile as is */ }

      const user: AuthUser = {
        userId:     me.id,
        tenantId:   me.tenantId,
        email:      me.email,
        fullName:   savedName ?? me.email,
        projectId:  me.projectId,
        token:      accessToken,
        userType:   me.userType,
        role:       me.role,
        tenantRole: me.tenantRole,
      };
      setUser(user);
      if (user.userType === 'client') {
        setPortalUser({
          userId: user.userId, tenantId: user.tenantId, email: user.email, fullName: user.fullName,
          projectId: user.projectId, role: user.role ?? 'client_user', clientName: '', token: accessToken,
        });
      }
      navigate(landingPathFor(user.userType), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not finish setting up your account.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--gray)' }}>
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <div
            className="w-8 h-8 rounded flex items-center justify-center text-white text-sm font-mono font-bold"
            style={{ backgroundColor: 'var(--blue)' }}
          >
            V
          </div>
          <span className="font-display text-2xl font-bold tracking-tight" style={{ color: 'var(--ink)' }}>Veridox</span>
        </div>

        <div className="card p-6 space-y-5">
          {phase.kind === 'checking' && <p className="text-sm text-muted">Checking your invite…</p>}

          {phase.kind === 'invalid' && (
            <>
              <div className="flex items-start gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm" role="alert">
                <AlertCircle size={14} className="shrink-0 mt-0.5" /> {phase.message}
              </div>
              <Link to="/login" className="btn-secondary w-full justify-center">Go to sign in</Link>
            </>
          )}

          {phase.kind === 'ready' && (
            <>
              <div>
                <h1 className="font-display text-lg font-semibold text-ink">Welcome to Veridox</h1>
                <p className="text-sm text-muted mt-0.5">
                  {phase.email ? <>Set a password for <span className="font-mono">{phase.email}</span> to join your project.</> : 'Set a password to join your project.'}
                </p>
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm" role="alert">
                  <AlertCircle size={14} className="shrink-0 mt-0.5" /> {error}
                </div>
              )}

              <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label mb-1.5" htmlFor="first-name">First name</label>
                    <input
                      id="first-name" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                      className="input" autoComplete="given-name" autoFocus
                    />
                  </div>
                  <div>
                    <label className="label mb-1.5" htmlFor="last-name">Last name</label>
                    <input
                      id="last-name" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)}
                      className="input" autoComplete="family-name"
                    />
                  </div>
                </div>
                <div>
                  <label className="label mb-1.5" htmlFor="new-password">Password</label>
                  <input
                    id="new-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    className="input" autoComplete="new-password"
                  />
                </div>
                <div>
                  <label className="label mb-1.5" htmlFor="confirm-password">Confirm password</label>
                  <input
                    id="confirm-password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    className="input" autoComplete="new-password"
                  />
                </div>
                <button type="submit" disabled={saving} className="btn-primary w-full justify-center">
                  <KeyRound size={15} /> {saving ? 'Setting up…' : 'Set password and continue'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
